import { useEffect, useMemo, useReducer, useRef, startTransition } from 'react';
import { METRICS_WARM_BATCH } from '../constants';
import type { SpectralWorkspaceSession } from '../types';
import type { SessionFullTractMetrics } from '../utils/computeSessionFullTractMetrics';
import {
  isSessionFullTractMetricsCached,
  resolveSessionFullTractMetrics,
  resolveSessionsFullTractMetrics,
} from '../utils/sessionFullTractMetricsCache';

export type { SessionFullTractMetrics };

const PLACEHOLDER_METRICS = (sessionId: number): SessionFullTractMetrics => ({
  sessionId,
  meanHr: null,
  meanRrMs: null,
  lfHfRatio: null,
  rmssdMs: null,
});

const scheduleBackgroundWork = (work: () => void) => {
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(work);
    return;
  }

  window.setTimeout(work, 0);
};

export const useSessionFullTractMetrics = (
  readySessions: SpectralWorkspaceSession[]
) => {
  const [warmVersion, bumpWarmVersion] = useReducer(
    (version: number) => version + 1,
    0
  );
  const readySessionsRef = useRef(readySessions);
  readySessionsRef.current = readySessions;

  const sessionFingerprint = useMemo(
    () =>
      readySessions
        .map(session => `${session.sessionId}:${session.events?.length ?? 0}`)
        .join('|'),
    [readySessions]
  );

  useEffect(() => {
    const sessions = readySessionsRef.current;
    if (sessions.length === 0) return;

    let cancelled = false;
    let index = 0;
    const timeoutIds: ReturnType<typeof setTimeout>[] = [];

    const warmNext = () => {
      if (cancelled) return;

      let computedInBatch = 0;

      while (index < sessions.length && computedInBatch < METRICS_WARM_BATCH) {
        const session = sessions[index];
        index += 1;

        if (!isSessionFullTractMetricsCached(session)) {
          resolveSessionFullTractMetrics(session);
          computedInBatch += 1;
        }
      }

      if (computedInBatch > 0) {
        startTransition(() => bumpWarmVersion());
      }

      if (index < sessions.length) {
        scheduleBackgroundWork(warmNext);
      }
    };

    const hasUncached = sessions.some(
      session => !isSessionFullTractMetricsCached(session)
    );

    if (hasUncached) {
      scheduleBackgroundWork(warmNext);
    }

    return () => {
      cancelled = true;
      timeoutIds.forEach(id => window.clearTimeout(id));
    };
  }, [sessionFingerprint]);

  const metrics = useMemo(() => {
    const allCached = readySessions.every(session =>
      isSessionFullTractMetricsCached(session)
    );

    if (allCached) {
      return resolveSessionsFullTractMetrics(readySessions).metrics;
    }

    return readySessions.map(session =>
      isSessionFullTractMetricsCached(session)
        ? resolveSessionFullTractMetrics(session)
        : PLACEHOLDER_METRICS(session.sessionId)
    );
  }, [readySessions, warmVersion]);

  const isWarming = useMemo(
    () =>
      readySessions.some(session => !isSessionFullTractMetricsCached(session)),
    [readySessions, warmVersion]
  );

  return { metrics, isWarming };
};
